export const FORM_DATE_FORMAT_STRING = 'DD/MM/YYYY';
export const YEAR_MONTH_FIRST_DAY_FORMAT = 'YYYY-MM-01';
export const INVALID_DATE = 'Invalid date';

// Regex patterns
export const ALL_WHITESPACE_REGEX_TEST = /^\s*$/;
const TRAILING_WHITESPACE_REGEX = /\s$/;

/**
 * @template {Record<string, unknown>} TState
 * @typedef {Partial<TState>} StateUpdate
 */

/**
 * @template {Record<string, unknown>} TState
 * @typedef {{
 *   [K in keyof TState]: { field: K, value: TState[K] }
 * }[keyof TState]} FieldUpdate
 */

/**
 * @template {Record<string, unknown>} TState
 * @typedef {FieldUpdate<TState> | { state: Partial<TState> }} GeneralAction
 */

/**
 * A generic reducer function that updates state by either:
 * - Setting a specific field to a new value
 * - Merging a partial state object
 *
 * @template {Record<string, unknown>} TState
 * @param {TState} previousState - The current state before the update
 * @param {GeneralAction<TState>} action - Either a field/value pair or a partial state
 * @returns {TState} - The updated state
 */
export function generalReducer(previousState, action) {
  if ('state' in action) {
    return { ...previousState, ...action.state };
  }

  const { field, value } = action;

  return { ...previousState, [field]: value };
}

/**
 * Validates whether the given input is a valid integer.
 *
 * @param {string} input - The input value to be validated.
 * @returns {boolean} True if the input is an empty string or a valid integer, false otherwise.
 */
export const isValidInteger = (input) => {
  // Checks if the input is a number
  // Input type number usually accepts ['e', 'E', '+', '-'] as valid characters
  const numericValue = Number(input);
  // Checks if the input has a decimal point
  const hasDecimalPoint = String(input).includes('.');
  // Checks if the input has trailing whitespace
  const hasTrailingWhitespace = TRAILING_WHITESPACE_REGEX.test(input);

  return !!numericValue && !hasDecimalPoint && !hasTrailingWhitespace;
};

/**
 * @typedef {false | 0 | "" | [] | null | undefined} Falsy
 */

/**
 * Returns the provided value if it is truthy; otherwise, returns the fallback value or null if both are falsy.
 * Primarily used to reduce complexity flags raised by SonarQube.
 *
 * @template {unknown} TValue
 * @template {unknown} TFallback
 * @param {TValue} value - The primary value to be evaluated.
 * @param {TFallback} [fallback] - The fallback value to return if the primary value is falsy.
 * @returns {TValue extends Falsy
 *   ? (TFallback extends undefined ? null : TFallback)
 *   : TValue} The primary value if truthy, otherwise the fallback or null.
 */
export const getValueOrFallback = (value, fallback) =>
  /** @type {any} */ (value || (fallback === undefined ? null : fallback));

/**
 * @param {Object} params
 * @param {Object} params.productData - product data containing name fields.
 * @param {string} [params.productData.item] - The specific item name of the product.
 * @param {string} [params.productData.name] - The general name of the product.
 * @param {string} [params.productData.form] - The health category of the product, used for consultations.
 * @param {string} [params.fallback='Unknown Item'] - The fallback name to return if no valid name is found.
 * @returns {string} The resolved product name, preferring `item`, then `name`, and falling back to `fallback`.
 */
export const getProductDetailedName = ({ productData, fallback = 'Unknown Item' }) => {
  if (typeof productData !== 'object') {
    return fallback;
  }

  if (productData?.name === 'consultation') {
    return `${productData?.form} consultation`;
  }

  return productData?.item || productData?.name || fallback;
};

/**
 * Checks if a value is a valid non-empty array.
 * @param {any} arr
 * @returns {boolean}
 */
export const isValidNonEmptyArray = (arr) => Array.isArray(arr) && arr.length > 0;
